import { validatePresentation, type Presentation } from "../../../../historical-agent-worker/src/historical-response-contract.js";
import {
  HISTORICAL_DASHBOARD_PANEL_PAIRS_V3, HISTORICAL_ENTITY_CODES, HISTORICAL_PERIODS,
  historicalDashboardRecipesEffectivelyMatch, isHistoricalDashboardRecipeV2, isHistoricalDashboardRecipeV3, isHistoricalDashboardSnapshot,
  type HistoricalDashboardPanelKey, type HistoricalDashboardRecipeV2, type HistoricalDashboardRecipeV3, type HistoricalDashboardSnapshotV1,
  type HistoricalEntityCode, type HistoricalPeriod, type HistoricalTrajectoryMode,
} from "./historicalAnalytics";

type Change<T> = { before: T | null; after: T } | null;
export type HistoricalRevisionDiff = {
  schemaVersion: "historical-dashboard-revision-diff/v1";
  entityCodes: Change<HistoricalEntityCode[]>;
  period: Change<HistoricalPeriod>;
  addedPanels: HistoricalDashboardPanelKey[];
  removedPanels: HistoricalDashboardPanelKey[];
  panelOrder: Change<HistoricalDashboardPanelKey[]>;
  trajectoryMode: Change<HistoricalTrajectoryMode>;
};
export type HistoricalRevisionMetadata = {
  presentation?: Presentation;
  presentationSha256?: string;
  revisionId: string;
  conversationId: string;
  requestId: string;
  parentRevisionId: string | null;
  restoredFromRevisionId: string | null;
  revisionSource: "model" | "control" | "undo" | "fork";
  recipe: HistoricalDashboardRecipeV2 | HistoricalDashboardRecipeV3;
  diff: HistoricalRevisionDiff | null;
  resultRef: string | null;
  terminalStatus: "persisted" | "not_saved";
};
export type HistoricalTerminal = {
  schemaVersion: "historical-terminal/v1";
  requestId: string;
  conversationId: string;
  revisionId: string;
} & ({ status: "persisted"; resultRef: string } | { status: "not_saved"; resultRef?: never });
export type HistoricalRevisionResponse = {
  conversationId: string;
  terminal: HistoricalTerminal;
  snapshot: HistoricalDashboardSnapshotV1 | null;
  receipt: string;
  messageId: string;
  revision: HistoricalRevisionMetadata;
};

const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const exact = (value: unknown, keys: string[]): value is Record<string, unknown> => object(value)
  && Object.keys(value).length === keys.length && keys.every(key => Object.hasOwn(value, key));
const uuid = (value: unknown): value is string => typeof value === "string" && /^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/i.test(value);
const ref = (value: unknown): value is string => typeof value === "string" && /^har_[a-f\d]{64}$/.test(value);
const panels = HISTORICAL_DASHBOARD_PANEL_PAIRS_V3.map(panel => panel.panelKey);
const modes = ["entry_indexed_log", "annualized_revenue_calendar"];
const enumValue = (values: readonly string[]) => (value: unknown): boolean => typeof value === "string" && values.includes(value);
const enumList = (values: readonly string[], min = 0) => (value: unknown): boolean => Array.isArray(value)
  && value.length >= min && value.length <= values.length && new Set(value).size === value.length && value.every(enumValue(values));
const change = (value: unknown, check: (value: unknown) => boolean): boolean => value === null
  || (exact(value, ["before", "after"]) && (value.before === null || check(value.before)) && check(value.after));

export function isHistoricalRevisionDiff(value: unknown): value is HistoricalRevisionDiff {
  return exact(value, ["schemaVersion", "entityCodes", "period", "addedPanels", "removedPanels", "panelOrder", "trajectoryMode"])
    && value.schemaVersion === "historical-dashboard-revision-diff/v1"
    && change(value.entityCodes, enumList(HISTORICAL_ENTITY_CODES, 1))
    && change(value.period, enumValue(HISTORICAL_PERIODS))
    && enumList(panels)(value.addedPanels) && enumList(panels)(value.removedPanels)
    && change(value.panelOrder, enumList(panels, 1)) && change(value.trajectoryMode, enumValue(modes));
}
export function isHistoricalRevisionMetadata(value: unknown, allowLegacyNullDiff = false): value is HistoricalRevisionMetadata {
  const descriptor = object(value) && Object.hasOwn(value,"presentation");
  if(descriptor){try{validatePresentation(value.presentation);if(typeof value.presentationSha256!=="string"||! /^[0-9a-f]{64}$/.test(value.presentationSha256))return false;}catch{return false;}}
  return exact(value, [...(descriptor?["presentation","presentationSha256"]:[]),"revisionId", "conversationId", "requestId", "parentRevisionId", "restoredFromRevisionId", "revisionSource", "recipe", "diff", "resultRef", "terminalStatus"])
    && uuid(value.revisionId) && uuid(value.conversationId) && uuid(value.requestId)
    && (value.parentRevisionId === null || uuid(value.parentRevisionId))
    && (value.restoredFromRevisionId === null || uuid(value.restoredFromRevisionId))
    && ["model", "control", "undo", "fork"].includes(String(value.revisionSource))
    && (isHistoricalDashboardRecipeV2(value.recipe) || isHistoricalDashboardRecipeV3(value.recipe)) && (isHistoricalRevisionDiff(value.diff) || (allowLegacyNullDiff && value.diff === null))
    && ((value.terminalStatus === "persisted" && ref(value.resultRef)) || (value.terminalStatus === "not_saved" && value.resultRef === null));
}
export function revisionMatchesSnapshot(revision: HistoricalRevisionMetadata, snapshot: unknown): snapshot is HistoricalDashboardSnapshotV1 {
  return revision.terminalStatus === "persisted" && isHistoricalDashboardSnapshot(snapshot)
    && revision.resultRef === snapshot.resultRef
    && historicalDashboardRecipesEffectivelyMatch(revision.recipe, snapshot.payload.recipe);
}
export function isHistoricalRevisionResponse(value: unknown, requestId: string, conversationId?: string): value is HistoricalRevisionResponse {
  if (!exact(value, ["conversationId", "terminal", "snapshot", "receipt", "messageId", "revision"])
    || !isHistoricalRevisionMetadata(value.revision) || typeof value.receipt !== "string" || !uuid(value.messageId)) return false;
  const revision = value.revision, terminal = value.terminal;
  return exact(terminal, ["schemaVersion", "requestId", "conversationId", "revisionId", "status", ...(revision.terminalStatus === "persisted" ? ["resultRef"] : [])])
    && terminal.schemaVersion === "historical-terminal/v1" && terminal.requestId === requestId && revision.requestId === requestId
    && value.conversationId === revision.conversationId && terminal.conversationId === revision.conversationId
    && (!conversationId || revision.conversationId === conversationId)
    && terminal.revisionId === revision.revisionId && terminal.status === revision.terminalStatus
    && (revision.terminalStatus === "persisted"
      ? terminal.resultRef === revision.resultRef && revisionMatchesSnapshot(revision, value.snapshot)
      : value.snapshot === null);
}

export function ownedHistoricalRevisionState(value: unknown, conversationId: string): {
  active: HistoricalRevisionMetadata | null;
  revisions: Record<string, HistoricalRevisionMetadata>;
} {
  if (!object(value) || !object(value.conversation) || value.conversation.id !== conversationId) throw Error("The saved thread identity could not be verified.");
  const hasRevisionFields = ["active_revision_id", "active_revision", "revisions_by_id"].some(key => Object.hasOwn(value, key));
  if (!hasRevisionFields) {
    if (isHistoricalDashboardSnapshot(value.snapshot) && (value.snapshot.payload.recipe.schemaVersion === "historical-dashboard-recipe/v2" || value.snapshot.payload.recipe.schemaVersion === "historical-dashboard-recipe/v3")) throw Error("The active revision is missing.");
    return { active: null, revisions: {} };
  }
  // Stored v1 history has no authored diff. Only the owned read path may retain
  // that absence; new action terminal responses still require the closed diff.
  if (!object(value.revisions_by_id) || !Object.entries(value.revisions_by_id).every(([id, item]) => isHistoricalRevisionMetadata(item, true)
    && id === item.revisionId && item.conversationId === conversationId)) throw Error("The saved revision metadata is invalid.");
  const revisions = value.revisions_by_id as Record<string, HistoricalRevisionMetadata>;
  if (value.active_revision_id === null && value.active_revision === null && value.snapshot === null) return { active: null, revisions };
  const active = value.active_revision;
  if (!isHistoricalRevisionMetadata(active, true) || active.revisionId !== value.active_revision_id || active.conversationId !== conversationId
    || !revisionMatchesSnapshot(active, value.snapshot) || JSON.stringify(revisions[active.revisionId]) !== JSON.stringify(active)) throw Error("The active revision does not match the owned snapshot.");
  return { active, revisions };
}

export function historicalRevisionTrace(revision: HistoricalRevisionMetadata, snapshot: HistoricalDashboardSnapshotV1): Record<string, unknown> {
  return {
    schemaVersion: "historical-decision-trace/v1", requestId: revision.requestId,
    conversationId: revision.conversationId, revisionId: revision.revisionId, resultRef: revision.resultRef,
    intentKey: "custom_historical_dashboard", queryKeys: revision.recipe.panels.map(panel => panel.queryKey),
    definitionSha256s: snapshot.payload.panels.map(panel => panel.queryDefinitionSha256), datasetSha256: snapshot.datasetSha256,
    cohortKey: revision.recipe.cohortKey, metricKey: revision.recipe.metricKey, basis: revision.recipe.basis,
    validation: "passed", revisionSource: revision.revisionSource, diff: revision.diff,
    parentRevisionId: revision.parentRevisionId, restoredFromRevisionId: revision.restoredFromRevisionId, caveatKeys: ["actuals_only"],
    modelId: undefined, provider: "openrouter", normalizedRecipe: revision.recipe,
    definitionKeys: revision.recipe.panels.map(panel => panel.queryKey),
    definitionVersions: revision.recipe.panels.map(() => "v1"),
    datasetVersion: snapshot.payload.datasetVersion, sourceDate: snapshot.asOf,
    coverage: { panelCount: revision.recipe.panels.length },
    methodNames: revision.recipe.panels.map(panel => panel.queryKey),
    validationEvents: ["recipe_canonicalized", "definitions_pinned", "payload_hashed"],
    snapshotLineage: { payloadSha256: snapshot.payloadSha256, datasetSha256: snapshot.datasetSha256, rootQueryDefinitionSha256: snapshot.rootQueryDefinitionSha256 },
    latencyStages: ["interpreting", "validating_recipe", "running_queries", "assembling_snapshot", "persisting"],
  };
}
